import type { Ref } from 'vue'
import type { AssessmentTemplateListRow } from './interfaces'

import { toPaginationValue } from '@shared/lib/routes'
import { withCount } from '@shared/lib/format'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplateDeleteApi } from '../../api/assessment-templates/delete.hook'
import { useAssessmentTemplatesApi } from '../../api/assessment-templates/list.hook'

interface ModalDeleteAssessmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTemplate: ComputedRef<AssessmentTemplateListRow | null>
  refIsBlocked: ComputedRef<boolean>
  refBlockedCount: ComputedRef<number | null>
  refBlockedCountLabel: ComputedRef<string>
  closeModal: () => void
  confirmDelete: () => Promise<void>
  openDuplicate: () => void
}

/**
 * The delete confirmation, and the home of the `409` guard (§5). A template that already has
 * assessments cannot be deleted, and that is not an error to report and dismiss: the modal stays
 * open, states how many assessments hold it, and offers *duplicate* as the safe way to revise it.
 */
export const useModalDeleteAssessmentTemplate = (): ModalDeleteAssessmentTemplateReturn => {
  const store = useAssessmentTemplatesStore()
  const route = useRoute()
  const router = useRouter()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => store.isModalDelete)
  const refTemplate = computed<AssessmentTemplateListRow | null>(() => store.getTemplateToDelete)
  const refIsBlocked = computed<boolean>(() => store.isDeleteBlocked)
  const refBlockedCount = computed<number | null>(() => store.getDeleteBlockedAssessmentsCount)

  /** The guard's count is routinely 1, so the noun has to agree. */
  const refBlockedCountLabel = computed<string>(() =>
    withCount(refBlockedCount.value ?? 0, { one: 'assessment', other: 'assessments' }),
  )

  /** `setTemplateToDelete(null)` also clears the blocked count, so the next row never inherits
   *  this row's message. */
  const closeModal = (): void => {
    store.setTemplateToDelete(null)
  }

  /**
   * Refetches the list after a delete, stepping back a page first when the deleted row was the
   * page's last one. Without that the refetch asks for a page that no longer exists, and the table
   * renders its empty state while dozens of templates sit one page back — nothing else clamps it,
   * since `list.hook` reads `page` from the query and no response `current_page` is ever stored.
   * The page is read exactly as `list.hook` reads it, so the two cannot disagree.
   */
  const refetchAfterDelete = async (): Promise<void> => {
    const page = toPaginationValue(route.query?.page, store.getCurrentPage)

    if (store.getTemplates.length <= 1 && page > 1) {
      const previousPage = page - 1
      store.setCurrentPage(previousPage)
      await router.push({
        query: {
          ...route.query,
          page: String(previousPage),
        },
      })
    }

    await useAssessmentTemplatesApi()
  }

  const confirmDelete = async (): Promise<void> => {
    const template = refTemplate.value
    if (!template || refIsLoading.value) return

    // The refetch runs after the delete call rather than inside `onSuccess`, because it awaits a
    // `router.push` — a callback the API hook does not await would leave the request in flight.
    let isDeleted = false

    await useAssessmentTemplateDeleteApi({
      templateId: template.id,
      onStart: () => { refIsLoading.value = true },
      onSuccess: () => {
        closeModal()
        isDeleted = true
      },
      // No toast was fired for the guard — this modal owns the copy, and stays open.
      onBlocked: (assessmentsCount) => {
        store.setDeleteBlockedAssessmentsCount(assessmentsCount)
      },
      // Everything else was already toasted by the hook; just close.
      onError: () => {
        closeModal()
      },
      onFinish: () => { refIsLoading.value = false },
    })

    if (isDeleted) {
      await refetchAfterDelete()
    }
  }

  /** Hands the same row to the duplicate modal — the escape hatch from the guard. */
  const openDuplicate = (): void => {
    const template = refTemplate.value
    if (!template) return

    closeModal()
    store.setTemplateToDuplicate(template)
  }

  return {
    refIsOpen,
    refIsLoading,
    refTemplate,
    refIsBlocked,
    refBlockedCount,
    refBlockedCountLabel,
    closeModal,
    confirmDelete,
    openDuplicate,
  }
}
